import React from "react";

import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { useDatasetMappedValue } from "hooks/useDatasetMappedValue";
import { DatasetDataInfo } from "models/dataset-models";
import { formatDatasetValue } from "utils/value-format-utils";
import { useDatasetMappedIcon } from "hooks/useDatasetMappedIcon";
import { I } from "services/lang/LanguageService";

type DatasetDisplayValueProps = {
  dataset: DatasetDataInfo | undefined;
  data: InstantDoubleValue | null | undefined;
  loading: boolean;
  isData: boolean;
  truncateSize?: number;
};

const DatasetDisplayValue: React.FC<DatasetDisplayValueProps> = ({
  dataset,
  data,
  loading,
  isData,
  truncateSize,
}) => {
  if (loading || !dataset) {
    return (
      <span className="px-3 py-1">
        <i className="pi pi-spin pi-spinner" />
      </span>
    );
  }

  if (!isData) {
    return (
      <>
        <span className="font-bold">N/A</span>
        <span className="font-italic"> {dataset.unit}</span>
      </>
    );
  }

  if (dataset.stringValueMapper) {
    return (
      <MappedValue mapValue={data?.value} mapperId={dataset.stringValueMapper} truncateSize={truncateSize} />
    );
  }

  return (
    <>
      <span className="font-bold">{formatDatasetValue(dataset, data)}</span>
      <span className="font-italic"> {dataset.unit}</span>
    </>
  );
};

export default DatasetDisplayValue;

type MappedValueProps = {
  mapValue: number | undefined;
  mapperId: number;
  truncateSize?: number;
};

export const MappedValue: React.FC<MappedValueProps> = ({ mapValue, mapperId, truncateSize = 10 }) => {
  const { value: mappedValue, isLoading: mappedValueLoading } = useDatasetMappedValue(mapValue, mapperId);
  const mappedIcon = useDatasetMappedIcon(mapValue, mapperId);

  if (mappedIcon) {
    return <i className={mappedIcon as string} />;
  }

  if (!mappedValue && !mappedValueLoading) {
    return (
      <span className="px-3 py-1">
        {mapValue} <i className="pi pi-info-circle" />
      </span>
    );
  }
  if (mappedValueLoading) {
    return (
      <span className="px-3 py-1">
        <i className="pi pi-spin pi-spinner" />
      </span>
    );
  }

  return (
    <span
      className="truncate-text-mapped-value"
      style={{
        maxWidth: `${truncateSize}ch`,
      }}
    >
      {typeof mappedValue === "string" ? I(mappedValue) : mappedValue}
    </span>
  );
};
